// Caches to avoid duplicate fetches.
const mappingCache = {}; // key: bundled JS file URL, value: Promise resolving to parsed mappings.
const sourceCache = {}; // key: original file URL, value: array of source lines.

// Base64 VLQ decoding.
const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const base64Map = {};
for (let i = 0; i < base64Chars.length; i++) {
	base64Map[base64Chars[i]] = i;
}

function decodeVLQ (str, index) {
	let result = 0;
	let shift = 0;
	let digit;
	let continuation;
	do {
		digit = base64Map[str.charAt(index++)];
		continuation = digit & 32; // 6th bit indicates continuation.
		digit &= 31; // lower 5 bits.
		result += digit << shift;
		shift += 5;
	} while (continuation);
	const sign = result & 1 ? -1 : 1;
	result = result >> 1;
	return { value: result * sign, nextIndex: index };
}

// Parse the source map's mappings string.
function parseMappings (mappingsStr, sources, names) {
	const lines = mappingsStr.split(';');
	const mappings = [];
	let prevGeneratedColumn = 0;
	let prevSource = 0;
	let prevOriginalLine = 0;
	let prevOriginalColumn = 0;
	let prevName = 0;
	for (let generatedLine = 0; generatedLine < lines.length; generatedLine++) {
		const line = lines[generatedLine];
		prevGeneratedColumn = 0;
		if (!line) {
			continue;
		}
		const segments = line.split(',');
		for (const segment of segments) {
			if (!segment) {
				continue;
			}
			let index = 0;
			let decoded = decodeVLQ(segment, index);
			const generatedColumn = prevGeneratedColumn + decoded.value;
			index = decoded.nextIndex;
			prevGeneratedColumn = generatedColumn;
			const mapping = { generatedLine, generatedColumn };

			// If additional fields are present, decode them.
			if (index < segment.length) {
				decoded = decodeVLQ(segment, index);
				const sourceIndex = prevSource + decoded.value;
				index = decoded.nextIndex;
				prevSource = sourceIndex;
				mapping.source = sources[sourceIndex];

				decoded = decodeVLQ(segment, index);
				const originalLine = prevOriginalLine + decoded.value;
				index = decoded.nextIndex;
				prevOriginalLine = originalLine;
				mapping.originalLine = originalLine;

				decoded = decodeVLQ(segment, index);
				const originalColumn = prevOriginalColumn + decoded.value;
				index = decoded.nextIndex;
				prevOriginalColumn = originalColumn;
				mapping.originalColumn = originalColumn;

				if (index < segment.length) {
					decoded = decodeVLQ(segment, index);
					const nameIndex = prevName + decoded.value;
					index = decoded.nextIndex;
					prevName = nameIndex;
					mapping.name = names[nameIndex];
				}
			}
			mappings.push(mapping);
		}
	}
	return mappings;
}

// For a given generated position (0-indexed), find the mapping with the greatest
// generatedColumn that does not exceed the provided column.
function findMappingFor (mappings, line, column) {
	let candidate = null;
	for (const mapping of mappings) {
		if (mapping.generatedLine === line && mapping.generatedColumn <= column) {
			if (!candidate || mapping.generatedColumn > candidate.generatedColumn) {
				candidate = mapping;
			}
		}
	}
	return candidate;
}

// Remove any ":formatted:" marker and leading "../" segments from file paths.
function preprocessFilePath (filePath) {
	return filePath.replace(/:formatted:/, ':').replace(/^(\.\.\/)+/, '');
}

// Extract the sourceMappingURL from the JS file content.
function extractSourceMapUrl (jsContent) {
	const regex = /\/\/[#@]\s*sourceMappingURL=(\S+)/;
	const match = jsContent.match(regex);
	return match ? match[1] : null;
}

// Given the URL of a bundled JS file, fetch it and resolve its sourceMappingURL.
function fetchSourceMapUrlFromScript (scriptUrl) {
	return fetch(scriptUrl)
		.then((response) => {
			if (!response.ok) {
				throw new Error('Unable to fetch JS file: ' + scriptUrl);
			}
			return response.text();
		})
		.then((text) => {
			const mapUrl = extractSourceMapUrl(text);
			if (!mapUrl) {
				throw new Error('No sourceMappingURL found in ' + scriptUrl);
			}
			return new URL(mapUrl, scriptUrl).toString();
		});
}

// For a given bundled JS file URL, return a promise resolving to its parsed mappings array.
function getMappingsForFile (fileUrl) {
	if (mappingCache[fileUrl]) {
		return mappingCache[fileUrl];
	}
	const promise = fetchSourceMapUrlFromScript(fileUrl)
		.then(sourceMapUrl => fetch(sourceMapUrl))
		.then((response) => {
			if (!response.ok) {
				throw new Error('Unable to fetch source map for ' + fileUrl);
			}
			return response.json();
		})
		.then((sourceMap) => {
			return parseMappings(sourceMap.mappings, sourceMap.sources, sourceMap.names || []);
		})
		.catch((err) => {
			console.error(err);
			return null;
		});
	mappingCache[fileUrl] = promise;
	return promise;
}

// Fetch and cache an original source file's contents as an array of lines.
async function getSourceLines (file) {
	if (!sourceCache[file]) {
		try {
			const response = await fetch(file);
			if (!response.ok) {
				return [];
			}
			const text = await response.text();
			sourceCache[file] = text.split(/\r?\n/);
		} catch (e) {
			return [];
		}
	}
	return sourceCache[file];
}


// Helper: Parse a single stack trace line using multiple regex patterns.
function parseStackLine (line) {
	line = line.trim();
	let callee;
	let fileLineColumn = [];
	let native = false;
	let planA;
	let planB;

	if (
		(planA = line.match(/at (.+) \(eval at .+ \((.+)\), .+\)/)) // eval calls
		|| (planA = line.match(/at (.+) \((.+)\)/))
		|| ((line.slice(0, 3) !== 'at ') && (planA = line.match(/(.*)@(.*)/)))
	) {
		callee = planA[1];
		native = (planA[2] === 'native');
		fileLineColumn
	= (planA[2].match(/(.*):(\d+):(\d+)/)
		|| planA[2].match(/(.*):(\d+)/) || [])
				.slice(1);
	} else if ((planB = line.match(/^(at\s+)*(.+):(\d+):(\d+)/))) {
		fileLineColumn = planB.slice(2);
	} else {
		return undefined;
	}

	// Detect cases like Array.reduce where we expect built-in functions.
	if (callee && !fileLineColumn[0]) {
		const type = callee.split('.')[0];
		if (type === 'Array') {
			native = true;
		}
	}

	return {
		beforeParse: line,
		callee: callee || '',
		// If running in a browser and the file URL equals window.location.href.
		index: typeof window !== 'undefined' && (fileLineColumn[0] === window.location.href),
		native: native || false,
		file: fileLineColumn[0] ? preprocessFilePath(fileLineColumn[0]) : '',
		line: parseInt(fileLineColumn[1] || '', 10) || undefined,
		column: parseInt(fileLineColumn[2] || '', 10) || undefined
	};
}

// Existing functions (decodeVLQ, parseMappings, findMappingFor, etc.) remain unchanged.

// Updated transformStack that uses parseStackLine:
async function transformStack (stack) {
	const lines = stack.split('\n');
	const formattedLines = [];
	const items = [];

	// Use parseStackLine to process each line.
	const entries = lines
		.map(line => parseStackLine(line))
		.filter(x => x !== undefined);

	// Process each parsed entry asynchronously.
	await Promise.all(
		entries.map(async (entry) => {
			// If the entry has a file and both line and column info.
			if (entry.file && entry.line && entry.column) {
				const filePath = entry.file.replace(/^@/, ''); // Remove any leading "@".
				const generatedLineNumber = entry.line;
				const generatedColumnNumber = entry.column;

				// Get the source map mappings for this bundled file.
				const mappings = await getMappingsForFile(filePath);
				if (mappings) {
				// Adjust generated positions to 0-index.
					const mapping = findMappingFor(mappings, generatedLineNumber - 1, generatedColumnNumber - 1);
					if (mapping && mapping.source) {
						const mappedFile = preprocessFilePath(mapping.source);
						const originalLine = mapping.originalLine + 1;
						const originalColumn = mapping.originalColumn + 1;
						// Choose the function name based on the mapping or fallback to the parsed callee.
						const functionName = mapping.name || entry.callee;
						// Get the code snippet from the original source.
						const sourceLines = await getSourceLines(mappedFile);
						const snippet = (sourceLines[originalLine - 1] || '').trim();
						const fnPadded = functionName.padEnd(6, ' ');
						const locationStr = `${mappedFile}:${originalLine}`;
						const formattedLine = `at ${fnPadded} ${locationStr}      ${snippet}`;

						formattedLines.push(formattedLine);
						items.push({
							fileName: mappedFile,
							lineNumber: originalLine,
							columnNumber: originalColumn,
							originalCode: snippet
						});
						return;
					}
				}
			}
			// Fallback: if parsing or mapping fails, push the original unmodified line.
			formattedLines.push(entry.beforeParse);
		})
	);

	return {
		stackFrameParsed: formattedLines.join('\n'),
		items
	};
}

/**
 * Remaps an error’s stack trace by inferring the source map for each frame.
 *
 * @param {Error} error - The error object (with a .stack property).
 * @returns {Promise<Object>} - A promise resolving to an object with keys:
 *    - stackFrameParsed {string}
 *    - items {Array} of objects with keys: fileName, columnNumber, lineNumber, originalCode
 */
export function mapErrorStack (error) {
	if (!error || !error.stack) {
		return Promise.reject(new Error('Invalid error object; missing stack property.'));
	}
	return transformStack(error.stack);
}
